import React, { useState, useEffect, useRef } from "react";
const LockIcons = ({ size = 24 }) => {
    const src = (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo.jpg` : '/logo.jpg');
    const fallback = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><rect width='24' height='24' rx='4' fill='%23111' /><text x='50%' y='55%' font-size='12' font-family='Arial' fill='%23fff' text-anchor='middle' alignment-baseline='middle'>P</text></svg>";
    return (
        <img
            src={src}
            alt="logo"
            width={size}
            height={size}
            style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = fallback;
            }}
        />
    );
};
// Icônes SVG personnalisées
const LockIcon = ({ size = 24 }) => (
    // Cadenas outline simple et lisible (utilise currentColor)
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect x="3" y="10" width="18" height="11" rx="2" />
        <path d="M7 10V8a5 5 0 0110 0v2" />
        <circle cx="12" cy="15" r="1.5" fill="currentColor" stroke="none" />
    </svg>
);

const UserIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const KeyIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
);

const RefreshIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
);

const AlertIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const ShieldIcon = ({ size = 24 }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

export default function Login({ onLoginSuccess }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [requires2fa, setRequires2fa] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const [code, setCode] = useState("");
    const [resendCountdown, setResendCountdown] = useState(0);
    const countdownRef = useRef(null);
    const [debugInfo, setDebugInfo] = useState([]);

    // États pour la configuration 2FA
    const [showSetup2FA, setShowSetup2FA] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secret, setSecret] = useState('');
    const [setupCode, setSetupCode] = useState('');

    const addDebug = (msg) => {
        console.log('[LOGIN DEBUG]', msg);
        setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);
    };

    function buildUrl(path) {
        const base = (process.env.REACT_APP_API_URL || "http://localhost:5050").trim();
        if (!base) return path.startsWith("/") ? path : `/${path}`;
        return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
    }

    async function loginApi({ username, password }) {
        const url = buildUrl("/api/login");
        addDebug(`Appel API vers: ${url}`);
        console.log('[LOGIN] POST', url, { username: username ? '***' : '' }); // debug console

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });

        addDebug(`Status réponse: ${res.status}`);
        const data = await res.json();
        addDebug(`Données reçues: ${JSON.stringify(data)}`);

        if (!res.ok) {
            throw { message: data?.message || `Erreur ${res.status}` };
        }

        const requires2fa = Boolean(data.twoFactorRequired);
        const tempToken = username;
        const token = data.token || data.accessToken || data.access_token || data.jwt;

        addDebug(`2FA requis: ${requires2fa}, Token présent: ${!!token}`);

        return { requires2fa, tempToken, token, raw: data };
    }

    async function verify2faApi({ tempToken, code }) {
        const url = buildUrl("/api/verify-2fa");
        addDebug(`Vérification 2FA vers: ${url}`);
        console.log('[2FA] POST', url, { username: tempToken ? '***' : '', code: code ? '***' : '' });

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username: tempToken, code }),
        });

        addDebug(`Status 2FA: ${res.status}`);
        const data = await res.json();

        if (!res.ok) {
            throw { message: data?.message || `Erreur ${res.status}` };
        }

        const token = data.token || data.accessToken || data.access_token || data.jwt;
        addDebug(`Token 2FA reçu: ${!!token}`);

        return { token, raw: data };
    }

    async function setup2FAApi({ username }) {
        const url = buildUrl("/api/generate-2fa");
        addDebug(`Génération 2FA vers: ${url}`);
        console.log('[2FA-SETUP] POST', url, { username: username ? '***' : '' });

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username }),
        });

        addDebug(`Status génération 2FA: ${res.status}`);
        const data = await res.json();

        if (!res.ok) {
            throw { message: data?.message || `Erreur ${res.status}` };
        }

        return { secret: data.secret, qrUrl: data.qrUrl };
    }

    async function activate2FAApi({ username, secret, code }) {
        const url = buildUrl("/api/activate-2fa");
        addDebug(`Activation 2FA vers: ${url}`);
        console.log('[2FA-ACTIVATE] POST', url, { username: username ? '***' : '', secret: secret ? '***' : '', code: code ? '***' : '' });

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, secret, code }),
        });

        addDebug(`Status activation 2FA: ${res.status}`);
        const data = await res.json();

        if (!res.ok) {
            throw { message: data?.message || `Erreur ${res.status}` };
        }

        return data;
    }

    const startCountdown = (seconds) => {
        setResendCountdown(seconds);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        addDebug("Composant Login monté");
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    const handleSubmitLogin = async (e) => {
        if (e) e.preventDefault();
        setError("");
        setLoading(true);

        try {
            addDebug(`Tentative de connexion: ${username}`);
            const res = await loginApi({ username, password });

            if (res.requires2fa) {
                addDebug("Mode 2FA activé");
                setRequires2fa(true);
                setTempToken(res.tempToken);
                startCountdown(60);
            } else {
                const user = res.raw?.user;

                if (!user?.two_factor_enabled) {
                    addDebug("2FA non configuré - redirection vers configuration obligatoire");
                    setError("Vous devez d'abord configurer Google Authenticator pour sécuriser votre compte.");
                    setSuccess("");

                    setTimeout(() => {
                        handleSetup2FA();
                    }, 1000);

                    setLoading(false);
                    return;
                }

                addDebug(`Connexion directe avec 2FA activé - Token reçu: ${res.token?.substring(0, 20)}...`);

                // IMPORTANT: Stocker le token même pour une connexion directe
                if (res.token) {
                    localStorage.setItem('token', res.token);
                    localStorage.setItem('authToken', res.token);
                    addDebug(`Token stocké dans localStorage: ${res.token.substring(0, 20)}...`);
                }

                // Stocker les informations utilisateur
                if (user) {
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('prospectys_user', JSON.stringify(user));
                    addDebug(`Utilisateur stocké: ${user.username}`);
                }

                if (onLoginSuccess) {
                    addDebug("Appel de onLoginSuccess()");
                    onLoginSuccess();
                } else {
                    addDebug("⚠️ ATTENTION: onLoginSuccess n'est pas défini!");
                }

                addDebug("Redirection vers /dashboard");
                setTimeout(() => {
                    addDebug("Exécution de la redirection...");
                    window.location.href = '/dashboard';
                }, 500);
            }
        } catch (err) {
            addDebug(`ERREUR: ${err.message}`);
            setError(err.message || "Erreur lors de la connexion");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit2fa = async (e) => {
        if (e) e.preventDefault();
        setError("");
        setLoading(true);

        try {
            addDebug(`Vérification 2FA pour: ${tempToken}`);
            const res = await verify2faApi({ tempToken, code });

            addDebug(`Token 2FA reçu: ${res.token?.substring(0, 20)}...`);

            // IMPORTANT: Stocker le token dans localStorage
            if (res.token) {
                localStorage.setItem('token', res.token);
                localStorage.setItem('authToken', res.token);
                addDebug(`Token stocké dans localStorage: ${res.token.substring(0, 20)}...`);
            } else {
                addDebug('⚠️ ATTENTION: Aucun token reçu de l\'API 2FA!');
            }

            // Stocker les informations utilisateur si disponibles
            if (res.raw?.user) {
                localStorage.setItem('user', JSON.stringify(res.raw.user));
                localStorage.setItem('prospectys_user', JSON.stringify(res.raw.user));
                addDebug(`Utilisateur stocké: ${res.raw.user.username}`);
            }

            if (onLoginSuccess) {
                addDebug("Appel de onLoginSuccess()");
                onLoginSuccess();
            } else {
                addDebug("⚠️ ATTENTION: onLoginSuccess n'est pas défini!");
            }

            addDebug("Rechargement vers /dashboard dans 500ms");
            setTimeout(() => {
                addDebug("Exécution du rechargement maintenant...");
                window.location.href = '/dashboard';
            }, 500);

        } catch (err) {
            addDebug(`ERREUR 2FA: ${err.message}`);
            setError(err.message || "Erreur de vérification 2FA");
        } finally {
            setLoading(false);
        }
    };

    const handleSetup2FA = async () => {
        if (!username) {
            setError('Veuillez entrer votre nom d\'utilisateur d\'abord');
            return;
        }

        setLoading(true);
        try {
            const result = await setup2FAApi({ username });
            setSecret(result.secret);
            setQrCodeUrl(result.qrUrl);
            setShowSetup2FA(true);
            setError('');
        } catch (err) {
            setError(err.message || 'Erreur lors de la génération du QR code');
        } finally {
            setLoading(false);
        }
    };

    const handleActivate2FA = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await activate2FAApi({ username, secret, code: setupCode });
            setShowSetup2FA(false);
            setSuccess('2FA activé avec succès ! Connexion automatique en cours...');
            setSetupCode('');

            addDebug("2FA activé - connexion automatique");
            setTimeout(async () => {
                try {
                    const res = await loginApi({ username, password });

                    if (res.token) {
                        // IMPORTANT: Stocker le token après activation 2FA
                        localStorage.setItem('token', res.token);
                        localStorage.setItem('authToken', res.token);
                        addDebug(`Token stocké après activation 2FA: ${res.token.substring(0, 20)}...`);

                        // Stocker les informations utilisateur
                        if (res.raw?.user) {
                            localStorage.setItem('user', JSON.stringify(res.raw.user));
                            localStorage.setItem('prospectys_user', JSON.stringify(res.raw.user));
                        }

                        if (onLoginSuccess) {
                            onLoginSuccess();
                        }

                        setTimeout(() => {
                            window.location.href = '/dashboard';
                        }, 500);
                    }
                } catch (err) {
                    setError("2FA activé mais erreur lors de la connexion. Veuillez vous reconnecter.");
                }
            }, 1500);

        } catch (err) {
            setError(err.message || 'Erreur lors de l\'activation du 2FA');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = () => {
        startCountdown(60);
        setError("Code 2FA renvoyé.");
    };

    const handleKeyPress = (e, submitFn) => {
        if (e.key === 'Enter') {
            submitFn();
        }
    };

    return (
        <>
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideIn {
                    from { opacity: 0; transform: translateX(-10px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(1deg); }
                }
                @keyframes glow {
                    0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
                    50% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.5); }
                }
                .fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                .slide-in {
                    animation: slideIn 0.4s ease-out;
                }
                .floating {
                    animation: float 6s ease-in-out infinite;
                }
                .glow-effect {
                    animation: glow 3s ease-in-out infinite;
                }
                
                /* Responsive design */
                @media (max-width: 768px) {
                    .mobile-header {
                        width: 64px !important;
                        height: 64px !important;
                        margin-bottom: 16px !important;
                    }
                    .mobile-title {
                        font-size: 24px !important;
                        margin-bottom: 8px !important;
                    }
                    .mobile-subtitle {
                        font-size: 14px !important;
                    }
                    .mobile-input {
                        padding: 14px 16px 14px 44px !important;
                        font-size: 16px !important;
                    }
                    .mobile-button {
                        padding: 16px !important;
                        font-size: 16px !important;
                    }
                    .mobile-modal {
                        padding: 24px !important;
                        margin: 10px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .mobile-container {
                        padding: 15px !important;
                    }
                    .mobile-card {
                        padding: 32px 24px !important;
                        border-radius: 20px !important;
                    }
                    .mobile-gap {
                        gap: 20px !important;
                    }
                }
            `}</style>

            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                minHeight: '100vh',
                width: '100vw',
                background: 'linear-gradient(135deg, rgba(30, 27, 75, 0.95) 0%, rgba(49, 46, 129, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                overflow: 'auto',
                position: 'relative'
            }}
                className="mobile-container"
            >
                {/* Globe 3D en arrière-plan - visible uniquement sur desktop */}
                {window.innerWidth > 768 && (
                    <iframe
                        src="/globe.html"
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            zIndex: 0,
                            opacity: 0.4,
                            pointerEvents: 'none'
                        }}
                    />
                )}

                {/* Overlay sombre - adaptatif */}
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: window.innerWidth > 768 ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
                    zIndex: 1,
                    pointerEvents: 'none'
                }} />

                {/* Carte principale - responsive */}
                <div
                    className="fade-in"
                    style={{
                        width: '100%',
                        maxWidth: window.innerWidth > 768 ? '480px' : 'calc(100% - 40px)',
                        background: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: window.innerWidth > 768 ? '32px' : '20px',
                        boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                        padding: window.innerWidth > 768 ? '56px 48px' : '40px 32px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        position: 'relative',
                        zIndex: 2,
                        margin: '20px',
                        minHeight: window.innerWidth <= 768 ? 'calc(100vh - 40px)' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-start'
                    }}
                >
                    {/* Logo/Header avec effet glow */}
                    <div style={{ textAlign: 'center', marginBottom: window.innerWidth > 768 ? '48px' : '32px' }}>
                        <div
                            className={`glow-effect ${window.innerWidth <= 768 ? 'mobile-header' : ''}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '96px',
                                height: '96px',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                borderRadius: '24px',
                                marginBottom: '24px',
                                color: 'white',
                                position: 'relative'
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                inset: '-2px',
                                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                                borderRadius: '24px',
                                opacity: 0.3,
                                filter: 'blur(8px)',
                                zIndex: -1
                            }} />
                            {requires2fa ?
                                <ShieldIcon size={window.innerWidth <= 768 ? 32 : 48} /> :
                                <LockIcons size={window.innerWidth <= 768 ? 32 : 48} />
                            }
                        </div>
                        <h1
                            className={window.innerWidth <= 768 ? 'mobile-title' : ''}
                            style={{
                                fontSize: '36px',
                                fontWeight: '800',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                marginBottom: '12px',
                                letterSpacing: '-1px'
                            }}
                        >
                            {requires2fa ? "Authentification" : "Connexion"}
                        </h1>
                        <p
                            className={window.innerWidth <= 768 ? 'mobile-subtitle' : ''}
                            style={{
                                color: '#64748b',
                                fontSize: '16px',
                                fontWeight: '500',
                                lineHeight: '1.5'
                            }}
                        >
                            {requires2fa
                                ? "Validez votre identité avec le code 2FA"
                                : "Accédez à votre espace sécurisé"}
                        </p>
                    </div>

                    {/* Formulaire Login */}
                    {!requires2fa ? (
                        <div
                            className={`slide-in ${window.innerWidth <= 480 ? 'mobile-gap' : ''}`}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: window.innerWidth > 768 ? '28px' : '24px'
                            }}
                        >
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: window.innerWidth > 768 ? '15px' : '14px',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    marginBottom: '12px',
                                    letterSpacing: '-0.2px'
                                }}>
                                    Identifiant
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{
                                        position: 'absolute',
                                        left: '18px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        display: 'flex',
                                        alignItems: 'center',
                                        zIndex: 1
                                    }}>
                                        <UserIcon />
                                    </div>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        onKeyPress={(e) => handleKeyPress(e, handleSubmitLogin)}
                                        placeholder="votre.identifiant"
                                        disabled={loading}
                                        className={window.innerWidth <= 768 ? 'mobile-input' : ''}
                                        style={{
                                            width: '100%',
                                            paddingLeft: '52px',
                                            paddingRight: '18px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: window.innerWidth > 768 ? '16px' : '12px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            outline: 'none',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxSizing: 'border-box',
                                            background: '#f8fafc'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#8b5cf6';
                                            e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                                            e.target.style.background = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.background = '#f8fafc';
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: window.innerWidth > 768 ? '15px' : '14px',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    marginBottom: '12px',
                                    letterSpacing: '-0.2px'
                                }}>
                                    Mot de passe
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{
                                        position: 'absolute',
                                        left: '18px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        display: 'flex',
                                        alignItems: 'center',
                                        zIndex: 1
                                    }}>
                                        <LockIcon size={20} />
                                    </div>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyPress={(e) => handleKeyPress(e, handleSubmitLogin)}
                                        placeholder="••••••••"
                                        disabled={loading}
                                        className={window.innerWidth <= 768 ? 'mobile-input' : ''}
                                        style={{
                                            width: '100%',
                                            paddingLeft: '52px',
                                            paddingRight: '18px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: window.innerWidth > 768 ? '16px' : '12px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            outline: 'none',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxSizing: 'border-box',
                                            background: '#f8fafc'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#8b5cf6';
                                            e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                                            e.target.style.background = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.background = '#f8fafc';
                                        }}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="fade-in" style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '14px',
                                    padding: '18px',
                                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                    border: '2px solid #fca5a5',
                                    borderRadius: '16px'
                                }}>
                                    <div style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }}>
                                        <AlertIcon />
                                    </div>
                                    <p style={{ fontSize: '14px', color: '#991b1b', margin: 0, fontWeight: '600', lineHeight: '1.5' }}>
                                        {error}
                                    </p>
                                </div>
                            )}

                            {success && (
                                <div className="fade-in" style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '14px',
                                    padding: '18px',
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    border: '2px solid #86efac',
                                    borderRadius: '16px'
                                }}>
                                    <div style={{ color: '#15803d', flexShrink: 0, marginTop: '2px' }}>
                                        <CheckCircleIcon />
                                    </div>
                                    <p style={{ fontSize: '14px', color: '#15803d', margin: 0, fontWeight: '600', lineHeight: '1.5' }}>
                                        {success}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleSubmitLogin}
                                disabled={loading}
                                className={window.innerWidth <= 768 ? 'mobile-button' : ''}
                                style={{
                                    width: '100%',
                                    padding: '18px',
                                    background: loading ? '#cbd5e1' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '17px',
                                    borderRadius: window.innerWidth > 768 ? '16px' : '12px',
                                    border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    boxShadow: loading ? 'none' : '0 12px 28px rgba(139, 92, 246, 0.4)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: 'translateY(0)',
                                    letterSpacing: '0.3px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading && window.innerWidth > 768) {
                                        e.target.style.transform = 'translateY(-3px)';
                                        e.target.style.boxShadow = '0 16px 36px rgba(139, 92, 246, 0.5)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (window.innerWidth > 768) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 12px 28px rgba(139, 92, 246, 0.4)';
                                    }
                                }}
                            >
                                {loading ? (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px'
                                    }}>
                                        <RefreshIcon />
                                        Connexion...
                                    </span>
                                ) : (
                                    "Se connecter"
                                )}
                            </button>

                            {/* Séparateur responsive */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                margin: window.innerWidth > 768 ? '8px 0' : '4px 0'
                            }}>
                                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                                <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>ou</span>
                                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                            </div>

                            <button
                                onClick={handleSetup2FA}
                                disabled={loading || !username}
                                style={{
                                    width: '100%',
                                    padding: window.innerWidth > 768 ? '16px' : '14px',
                                    background: 'white',
                                    border: '2px solid #e2e8f0',
                                    color: '#475569',
                                    fontWeight: '600',
                                    fontSize: window.innerWidth > 768 ? '15px' : '14px',
                                    borderRadius: window.innerWidth > 768 ? '16px' : '12px',
                                    cursor: loading || !username ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    flexDirection: window.innerWidth <= 480 ? 'column' : 'row'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading && username) {
                                        e.target.style.background = '#f8fafc';
                                        e.target.style.borderColor = '#8b5cf6';
                                        e.target.style.color = '#8b5cf6';
                                        if (window.innerWidth > 768) {
                                            e.target.style.transform = 'translateY(-2px)';
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.color = '#475569';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                <ShieldIcon size={20} />
                                <span style={{ textAlign: 'center' }}>
                                    {window.innerWidth <= 480 ? 'Configurer 2FA' : 'Configurer Google Authenticator'}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="slide-in" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                                border: '2px solid #93c5fd',
                                borderRadius: '16px',
                                padding: '20px',
                                marginBottom: '8px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                                    <div style={{ color: '#2563eb', flexShrink: 0, marginTop: '2px' }}>
                                        <CheckCircleIcon />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '15px', color: '#1e40af', margin: 0, fontWeight: '700', marginBottom: '6px' }}>
                                            Code de vérification envoyé
                                        </p>
                                        <p style={{ fontSize: '14px', color: '#3b82f6', margin: 0, fontWeight: '500', lineHeight: '1.5' }}>
                                            Consultez votre application Google Authenticator
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    marginBottom: '12px',
                                    letterSpacing: '-0.2px'
                                }}>
                                    Code de vérification à 6 chiffres
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{
                                        position: 'absolute',
                                        left: '18px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        display: 'flex',
                                        alignItems: 'center',
                                        zIndex: 1
                                    }}>
                                        <KeyIcon />
                                    </div>
                                    <input
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        onKeyPress={(e) => handleKeyPress(e, handleSubmit2fa)}
                                        placeholder="000 000"
                                        maxLength="6"
                                        disabled={loading}
                                        style={{
                                            width: '100%',
                                            paddingLeft: '52px',
                                            paddingRight: '18px',
                                            paddingTop: '20px',
                                            paddingBottom: '20px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '16px',
                                            fontSize: '24px',
                                            fontWeight: '700',
                                            textAlign: 'center',
                                            letterSpacing: '0.3em',
                                            outline: 'none',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxSizing: 'border-box',
                                            background: '#f8fafc'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#8b5cf6';
                                            e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                                            e.target.style.background = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.background = '#f8fafc';
                                        }}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="fade-in" style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '14px',
                                    padding: '18px',
                                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                    border: '2px solid #fca5a5',
                                    borderRadius: '16px'
                                }}>
                                    <div style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }}>
                                        <AlertIcon />
                                    </div>
                                    <p style={{ fontSize: '14px', color: '#991b1b', margin: 0, fontWeight: '600', lineHeight: '1.5' }}>
                                        {error}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleSubmit2fa}
                                disabled={loading || code.length !== 6}
                                style={{
                                    width: '100%',
                                    padding: '18px',
                                    background: loading || code.length !== 6 ? '#cbd5e1' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '17px',
                                    borderRadius: '16px',
                                    border: 'none',
                                    cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer',
                                    boxShadow: loading || code.length !== 6 ? 'none' : '0 12px 28px rgba(16, 185, 129, 0.4)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: 'translateY(0)',
                                    letterSpacing: '0.3px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading && code.length === 6) {
                                        e.target.style.transform = 'translateY(-3px)';
                                        e.target.style.boxShadow = '0 16px 36px rgba(16, 185, 129, 0.5)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 12px 28px rgba(16, 185, 129, 0.4)';
                                }}
                            >
                                {loading ? (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px'
                                    }}>
                                        <RefreshIcon />
                                        Vérification...
                                    </span>
                                ) : (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px'
                                    }}>
                                        <CheckCircleIcon />
                                        Valider le code
                                    </span>
                                )}
                            </button>

                            <button
                                onClick={handleResend}
                                disabled={resendCountdown > 0}
                                style={{
                                    width: '100%',
                                    padding: '16px',
                                    background: 'white',
                                    border: '2px solid #e2e8f0',
                                    color: resendCountdown > 0 ? '#94a3b8' : '#475569',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    borderRadius: '16px',
                                    cursor: resendCountdown > 0 ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                                onMouseEnter={(e) => {
                                    if (resendCountdown === 0) {
                                        e.target.style.background = '#f8fafc';
                                        e.target.style.borderColor = '#cbd5e1';
                                        e.target.style.transform = 'translateY(-2px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                {resendCountdown > 0 ? (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px'
                                    }}>
                                        <RefreshIcon />
                                        Renvoyer dans {resendCountdown}s
                                    </span>
                                ) : (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px'
                                    }}>
                                        <RefreshIcon />
                                        Renvoyer le code
                                    </span>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Footer responsive */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: window.innerWidth > 768 ? '40px' : '24px',
                        paddingTop: window.innerWidth > 768 ? '28px' : '20px',
                        borderTop: '2px solid #f1f5f9'
                    }}>
                        <p style={{
                            fontSize: window.innerWidth > 768 ? '14px' : '12px',
                            color: '#94a3b8',
                            margin: 0,
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            flexDirection: window.innerWidth <= 480 ? 'column' : 'row'
                        }}>
                            <LockIcon size={16} />
                            <span>Connexion sécurisée avec chiffrement SSL/TLS</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Modal de configuration 2FA responsive */}
            {showSetup2FA && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: window.innerWidth > 768 ? '20px' : '10px',
                    animation: 'fadeIn 0.3s ease-out'
                }}>
                    <div
                        className={`fade-in ${window.innerWidth <= 768 ? 'mobile-modal' : ''}`}
                        style={{
                            background: 'white',
                            borderRadius: window.innerWidth > 768 ? '24px' : '16px',
                            padding: window.innerWidth > 768 ? '40px' : '24px',
                            width: '100%',
                            maxWidth: window.innerWidth > 768 ? '560px' : '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.5)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '80px',
                                height: '80px',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                borderRadius: '20px',
                                marginBottom: '20px',
                                boxShadow: '0 12px 28px rgba(139, 92, 246, 0.4)',
                                color: 'white'
                            }}>
                                <ShieldIcon size={40} />
                            </div>
                            <h3 style={{
                                color: '#1e293b',
                                marginBottom: '12px',
                                fontSize: '28px',
                                fontWeight: '800',
                                letterSpacing: '-0.5px'
                            }}>
                                Configuration 2FA
                            </h3>
                            <p style={{ color: '#64748b', fontSize: '15px', margin: 0, fontWeight: '500' }}>
                                Sécurisez votre compte avec Google Authenticator
                            </p>
                        </div>

                        <div style={{ marginBottom: '32px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                borderRadius: '16px',
                                padding: '20px',
                                marginBottom: '24px',
                                border: '2px solid #e2e8f0'
                            }}>
                                <h4 style={{
                                    color: '#334155',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px',
                                        background: '#8b5cf6',
                                        color: 'white',
                                        borderRadius: '50%',
                                        fontSize: '12px',
                                        fontWeight: '800'
                                    }}>1</span>
                                    Téléchargez l'application
                                </h4>
                                <p style={{ color: '#64748b', marginBottom: '12px', lineHeight: '1.6', fontSize: '14px' }}>
                                    Installez Google Authenticator sur votre smartphone :
                                </p>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    <div style={{
                                        flex: '1',
                                        minWidth: '140px',
                                        padding: '12px 16px',
                                        background: 'white',
                                        borderRadius: '12px',
                                        border: '2px solid #e2e8f0',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#475569',
                                        textAlign: 'center'
                                    }}>
                                        📱 iOS - App Store
                                    </div>
                                    <div style={{
                                        flex: '1',
                                        minWidth: '140px',
                                        padding: '12px 16px',
                                        background: 'white',
                                        borderRadius: '12px',
                                        border: '2px solid #e2e8f0',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#475569',
                                        textAlign: 'center'
                                    }}>
                                        🤖 Android - Play Store
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                borderRadius: '16px',
                                padding: '20px',
                                marginBottom: '24px',
                                border: '2px solid #e2e8f0'
                            }}>
                                <h4 style={{
                                    color: '#334155',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px',
                                        background: '#8b5cf6',
                                        color: 'white',
                                        borderRadius: '50%',
                                        fontSize: '12px',
                                        fontWeight: '800'
                                    }}>2</span>
                                    Scannez le QR code
                                </h4>

                                <div style={{
                                    textAlign: 'center',
                                    padding: '24px',
                                    background: 'white',
                                    borderRadius: '16px',
                                    border: '3px dashed #cbd5e1',
                                    marginBottom: '16px'
                                }}>
                                    {qrCodeUrl ? (
                                        <img
                                            src={qrCodeUrl}
                                            alt="QR Code 2FA"
                                            style={{
                                                maxWidth: '220px',
                                                width: '100%',
                                                height: 'auto',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                    ) : (
                                        <div style={{ padding: '60px 20px', color: '#94a3b8', fontSize: '15px', fontWeight: '500' }}>
                                            <RefreshIcon />
                                            <div style={{ marginTop: '12px' }}>Génération du QR code...</div>
                                        </div>
                                    )}
                                </div>

                                <div style={{
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    border: '2px solid #fbbf24',
                                    borderRadius: '12px',
                                    padding: '16px'
                                }}>
                                    <p style={{
                                        margin: '0 0 8px 0',
                                        fontSize: '13px',
                                        color: '#92400e',
                                        fontWeight: '700',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        💡 Code manuel
                                    </p>
                                    <p style={{
                                        margin: '0 0 8px 0',
                                        fontSize: '16px',
                                        color: '#78350f',
                                        fontWeight: '800',
                                        fontFamily: 'monospace',
                                        letterSpacing: '1px',
                                        wordBreak: 'break-all'
                                    }}>
                                        {secret}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#92400e', fontWeight: '500' }}>
                                        Utilisez ce code si vous ne pouvez pas scanner le QR
                                    </p>
                                </div>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                borderRadius: '16px',
                                padding: '20px',
                                border: '2px solid #e2e8f0'
                            }}>
                                <h4 style={{
                                    color: '#334155',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px',
                                        background: '#8b5cf6',
                                        color: 'white',
                                        borderRadius: '50%',
                                        fontSize: '12px',
                                        fontWeight: '800'
                                    }}>3</span>
                                    Entrez le code de validation
                                </h4>
                                <p style={{ color: '#64748b', marginBottom: '16px', lineHeight: '1.6', fontSize: '14px' }}>
                                    Saisissez le code à 6 chiffres généré par l'application
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleActivate2FA}>
                            <div style={{ marginBottom: '24px' }}>
                                <input
                                    type="text"
                                    value={setupCode}
                                    onChange={(e) => setSetupCode(e.target.value)}
                                    placeholder="000 000"
                                    maxLength="6"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '20px',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '16px',
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        textAlign: 'center',
                                        letterSpacing: '0.3em',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        background: '#f8fafc',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#8b5cf6';
                                        e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                                        e.target.style.background = 'white';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.boxShadow = 'none';
                                        e.target.style.background = '#f8fafc';
                                    }}
                                />
                            </div>

                            {error && (
                                <div className="fade-in" style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                    padding: '16px',
                                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                    border: '2px solid #fca5a5',
                                    borderRadius: '12px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }}>
                                        <AlertIcon />
                                    </div>
                                    <p style={{ fontSize: '14px', color: '#991b1b', margin: 0, fontWeight: '600' }}>
                                        {error}
                                    </p>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '16px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowSetup2FA(false);
                                        setSetupCode('');
                                        setError('');
                                    }}
                                    style={{
                                        flex: '1',
                                        padding: '16px',
                                        background: 'white',
                                        color: '#475569',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '14px',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#f8fafc';
                                        e.target.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'white';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || setupCode.length !== 6}
                                    style={{
                                        flex: '1',
                                        padding: '16px',
                                        background: loading || setupCode.length !== 6 ? '#cbd5e1' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '14px',
                                        fontWeight: '700',
                                        fontSize: '16px',
                                        cursor: loading || setupCode.length !== 6 ? 'not-allowed' : 'pointer',
                                        boxShadow: loading || setupCode.length !== 6 ? 'none' : '0 8px 20px rgba(16, 185, 129, 0.3)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!loading && setupCode.length === 6) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 12px 28px rgba(16, 185, 129, 0.4)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                                    }}
                                >
                                    {loading ? (
                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                            <RefreshIcon />
                                            Activation...
                                        </span>
                                    ) : (
                                        'Activer 2FA'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}